// AdminCourseList.jsx
import React, { useEffect, useRef, useState } from "react";
import { toast } from "react-toastify";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faSearch,
  faPlus,
  faEdit,
  faFileImport,
  faFileExport,
  faChevronRight,
  faChevronLeft,
  faEye,
} from "@fortawesome/free-solid-svg-icons";
import {
  Row,
  Col,
  Card,
  Table,
  InputGroup,
  Form,
  Button,
  Modal,
} from "@themesberg/react-bootstrap";
import {
  adminAllCourseList,
  adminAddCourse,
  adminEditCourse,
  adminImportCourseList,
  adminExportCourseList,
  adminGetCourseById,
} from "../../../pages/api";

const ACTIVE_BUTTON_STYLE = {
  background: "#1D4E89",
  border: "none",
  borderRadius: "0.5rem",
  color: "#ffffff",
};

const mapCourseItem = (item) => ({
  id: item?.id ?? Date.now(),
  title: item?.title || "",
  price: item?.price || "",
  created_at: item?.created_at || "",
});


const capitalize = (value) =>
  String(value || "")
    .replace(/_/g, " ")
    .replace(/\b\w/g, (char) => char.toUpperCase());

export default function AdminCourseList() {
  const [search, setSearch] = useState("");
  const [courses, setCourses] = useState("");
  const [showModal, setShowModal] = useState(false);
  const [editingCourse, setEditingCourse] = useState(null);
  const startDateRef = useRef(null);
  const endDateRef = useRef(null);
  const enrollmentStartDateRef = useRef(null);
  const enrollmentEndDateRef = useRef(null);
  const [formData, setFormData] = useState({
    title: "",
    price: "",
    status: "",
    start_date: "",
    end_date: "",
    description: "",
    enrollment_start_date: "",
    enrollment_end_date: "",
  });
  const [errors, setErrors] = useState({
    title: "",
    price: "",
    status: "",
    start_date: "",
    // end_date: "",
  });
  const [loading, setLoading] = useState(false);
  const [editLoading, setEditLoading] = useState(false);
  const [viewLoading, setViewLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const importFileRef = useRef(null);
  const searchDebounceRef = useRef(null);
  const initialLoadRef = useRef(true);

  const [showViewModal, setShowViewModal] = useState(false);
  const [viewCourse, setViewCourse] = useState(null);

  const [pagination, setPagination] = useState({
    current_page: 1,
    last_page: 1,
    per_page: 10,
    total: 0,
  });

  const clearSearchDebounce = () => {
    if (searchDebounceRef.current) {
      clearTimeout(searchDebounceRef.current);
      searchDebounceRef.current = null;
    }
  };

  const fetchCourses = async (page = 1, perPage = 10, searchTerm = "") => {
    try {
      setLoading(true);

      const params = {
        page,
        per_page: perPage,
      };

      const trimmedSearch = String(searchTerm || "").trim();
      if (trimmedSearch) {
        params.search = trimmedSearch;
      }

      const response = await adminAllCourseList(params);
      const responseData = response?.data || {};
      const apiData = responseData?.data || {};
      const apiCourses = Array.isArray(apiData?.courses) ? apiData.courses : [];
      const apiPagination = apiData?.pagination || {};

      setCourses(apiCourses.length > 0 ? apiCourses.map(mapCourseItem) : []);
      setPagination({
        current_page: apiPagination.current_page ?? 1,
        last_page: apiPagination.last_page ?? 1,
        per_page: apiPagination.per_page ?? 10,
        total: apiPagination.total ?? apiCourses.length,
      });
      // setCourses(
      //   apiCourses.length > 0
      //     ? apiCourses.map(mapCourseItem).sort((a, b) => Number(b.id) - Number(a.id))
      //     : []
      // );
      // setCourses(
      //   apiCourses.length > 0
      //     ? apiCourses
      //       .map(mapCourseItem)
      //       .sort((a, b) => new Date(b.created_at || 0) - new Date(a.created_at || 0))
      //     : []
      // );
    } catch (error) {
      console.error("Failed to fetch courses:", error);
      toast.error(error?.response?.data?.message || "Unable to fetch course list.");
      setCourses([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (initialLoadRef.current) {
      initialLoadRef.current = false;
      fetchCourses(1, pagination.per_page, "");
      return;
    }

    clearSearchDebounce();
    searchDebounceRef.current = setTimeout(() => {
      fetchCourses(1, pagination.per_page, search);
    }, 300);

    return () => {
      clearSearchDebounce();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [search]);

  const filteredData = courses;

  const getShowingRange = () => {
    const total = pagination.total || 0;
    const current = pagination.current_page || 1;
    const perPage = pagination.per_page || 10;

    if (total === 0) {
      return { start: 0, end: 0 };
    }

    const start = (current - 1) * perPage + 1;
    const end = Math.min(current * perPage, total);

    return { start, end };
  };

  const getPageItems = () => {
    const totalPages = pagination.last_page || 1;
    const current = pagination.current_page || 1;

    if (totalPages <= 5) {
      return Array.from({ length: totalPages }, (_, i) => i + 1);
    }

    // Start pages
    if (current <= 2) {
      return [1, 2, 3, "...", totalPages];
    }

    // End pages
    if (current >= totalPages - 1) {
      return [1, "...", totalPages - 2, totalPages - 1, totalPages];
    }

    // Middle pages: 1 ... previous current next ... last
    return [1, "...", current - 1, current, current + 1, "...", totalPages];
  };

  const { start, end } = getShowingRange();

  const openAddModal = () => {
    setEditingCourse(null);
    setFormData({
      title: "",
      price: "",
      status: "",
      start_date: "",
      end_date: "",
      description: "",
      enrollment_start_date: "",
      enrollment_end_date: "",
    });
    setErrors({
      title: "",
      price: "",
      status: "",
      start_date: "",
      // end_date: "",
    });
    setShowModal(true);
  };

  // const openEditModal = (course) => {
  //   setEditingCourse(course);
  //   setFormData({
  //     title: course.title || "",
  //     price: course.price || "",
  //     status: course.status || "",
  //     start_date: course.start_date || "",
  //     end_date: course.end_date || "",
  //     description: course.description || "",
  //   });
  //   setErrors({
  //     title: "",
  //     price: "",
  //     status: "",
  //   });
  //   setShowModal(true);
  // };

  const openEditModal = async (course) => {
    try {
      setEditLoading(true);

      const response = await adminGetCourseById(course.id);
      const data = response?.data?.data;

      setEditingCourse(course);
      setFormData({
        title: data?.title || "",
        price: data?.price || "",
        status: data?.status || "",
        start_date: data?.start_date || "",
        end_date: data?.end_date || "",
        description: data?.description || "",
        enrollment_start_date: data?.enrollment_start_date || "",
        enrollment_end_date: data?.enrollment_end_date || "",
      });

      setErrors({
        title: "",
        price: "",
        status: "",
        start_date: "",
        // end_date: "",
      });

      setShowModal(true);
    } catch (error) {
      toast.error(error?.response?.data?.message || "Unable to fetch course details.");
    } finally {
      setEditLoading(false);
    }
  };


  const openViewModal = async (course) => {
    try {
      setViewLoading(true);
      const response = await adminGetCourseById(course.id);
      const data = response?.data?.data;

      setViewCourse(data);
      setShowViewModal(true);
    } catch (error) {
      toast.error(error?.response?.data?.message || "Unable to fetch course details.");
    } finally {
      setViewLoading(false);
    }
  };

  const closeModal = () => {
    setShowModal(false);
    setEditingCourse(null);
    setFormData({
      title: "",
      price: "",
      status: "",
      start_date: "",
      end_date: "",
      description: "",
      enrollment_start_date: "",
      enrollment_end_date: "",
    });
    setErrors({
      title: "",
      price: "",
      status: "",
      start_date: "",
      // end_date: "",
    });
  };

  const handleChange = (e) => {
    const { name, value } = e.target;

    // Stop non-numeric characters for price
    if (name === "price" && value !== "" && !/^\d*\.?\d*$/.test(value)) {
      return;
    }

    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));

    if (name === "title" || name === "price" || name === "status" || name === "start_date") {
      setErrors((prev) => ({
        ...prev,
        [name]: "",
      }));
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    const trimmedTitle = formData.title.trim();
    const trimmedPrice = formData.price.trim();
    const trimmedStatus = formData.status.trim();
    const trimmedStartDate = formData.start_date.trim();
    const newErrors = {
      title: !trimmedTitle ? "Course Title is required" : "",
      price: !trimmedPrice ? "Price is required" : "",
      status: !trimmedStatus ? "Status is required" : "",
      start_date: !trimmedStartDate ? "Start Date is required" : "",
      // end_date: !formData.end_date ? "End Date is required" : "",
    };

    setErrors(newErrors);

    // console.log("Form submission errors:", errors);

    if (newErrors.title || newErrors.price || newErrors.status || newErrors.start_date) {
      toast.error("Please fill all required fields.");
      return;
    }

    try {
      setSubmitting(true);

      const payload = {
        title: trimmedTitle,
        price: trimmedPrice,
        status: trimmedStatus,
        start_date: trimmedStartDate,
        end_date: formData.end_date || null,
        description: formData.description || null,
        enrollment_start_date: formData.enrollment_start_date || null,
        enrollment_end_date: formData.enrollment_end_date || null,
      };

      if (editingCourse) {
        const response = await adminEditCourse(editingCourse.id, payload);
        toast.success(response?.data?.message || "Course updated successfully.");
      } else {
        const response = await adminAddCourse(payload);
        toast.success(response?.data?.message || "Course added successfully.");
      }

      closeModal();
      // await fetchCourses();
      await fetchCourses(pagination.current_page, pagination.per_page, search);
    } catch (error) {
      console.error("Course save failed:", error);
      toast.error(error?.response?.data?.message || "Unable to save course.");
    } finally {
      setSubmitting(false);
    }
  };

  const handleImport = () => {
    if (importFileRef.current) {
      importFileRef.current.click();
    }
  };

  const handleImportFileChange = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    try {
      const formData = new FormData();
      formData.append("file", file);

      setSubmitting(true);
      const response = await adminImportCourseList(formData);
      toast.success(response?.data?.message || "Courses imported successfully.");
      // await fetchCourses();
      await fetchCourses(pagination.current_page, pagination.per_page, search);
    } catch (error) {
      // console.error("Import failed:", error);
      toast.error(error?.response?.data?.errors[0]?.errors[0] || "Unable to import courses.");
    } finally {
      setSubmitting(false);
      e.target.value = "";
    }
  };

  const handleExport = async () => {
    try {
      const response = await adminExportCourseList();

      const blob = new Blob([response.data], {
        type: response.headers["content-type"] || "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
      });

      const url = window.URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.download = "courses.xlsx";
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);

      toast.success("Courses exported successfully.");
    } catch (error) {
      // console.error("Export failed:", error);
      toast.error(error?.response?.data?.message || "Unable to export courses.");
    }
  };

  return (
    <>
      <div className="course-header py-4">
        <div className="course-header-title">
          {/* <h5 className="mb-0">Course List</h5> */}
          <h2 className="h4 mb-1">Course List</h2>
        </div>

        <div className="course-header-actions">
          <InputGroup className="course-search">
            <InputGroup.Text className="bg-white">
              <FontAwesomeIcon icon={faSearch} className="text-muted" />
            </InputGroup.Text>
            <Form.Control
              type="text"
              placeholder="Search"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              style={{ boxShadow: "none" }}
            />
          </InputGroup>

          <Button
            type="button"
            onClick={openAddModal}
            className="course-add-btn fw-semibold"
            style={ACTIVE_BUTTON_STYLE}
          >
            <FontAwesomeIcon icon={faPlus} className="me-2" />
            Add Course
          </Button>
        </div>
      </div>

      <Card className="border-0 shadow-sm rounded-4">
        <Card.Body className="p-4">
          <div className="course-list-top d-flex justify-content-between align-items-center mb-3 flex-wrap gap-2">
            <div>
              <h5 className="mb-1">Course List</h5>
              {/* <div className="text-muted small">Latest course entries appear on top</div> */}
            </div>

            <div className="course-list-actions d-flex align-items-center gap-2 flex-wrap">
              <input
                ref={importFileRef}
                type="file"
                accept=".xlsx,.csv"
                onChange={handleImportFileChange}
                style={{ display: "none" }}
              />
              <Button
                type="button"
                variant="outline-primary"
                className="fw-semibold px-3 py-2"
                onClick={handleImport}
              >
                <FontAwesomeIcon icon={faFileImport} className="me-2" />
                Import
              </Button>

              <Button
                type="button"
                variant="outline-primary"
                className="fw-semibold px-3 py-2"
                onClick={handleExport}
              >
                <FontAwesomeIcon icon={faFileExport} className="me-2" />
                Export
              </Button>
            </div>
          </div>

          <div className="table-responsive">
            <Table hover className="align-middle mb-0">
              <thead>
                <tr>
                  <th>Sr No</th>
                  <th>Course Title</th>
                  <th>Price</th>
                  <th className="text-center">Action</th>
                </tr>
              </thead>
              <tbody>
                {loading ? (
                  <tr>
                    <td colSpan={4} className="text-center text-muted py-5">
                      Loading courses...
                    </td>
                  </tr>
                ) : filteredData.length > 0 ? (
                  filteredData.map((item, index) => (
                    <tr key={item.id}>
                      {/* <td className="fw-semibold">{index + 1}</td> */}
                      <td className="fw-semibold">{start + index}</td>
                      <td>{item.title}</td>
                      <td>₹{Number(item.price || 0).toLocaleString("en-IN")}</td>
                      <td className="text-center">
                        <div className="d-flex gap-2 justify-content-center flex-wrap action-buttons">
                          <Button
                            type="button"
                            variant="link"
                            className="p-0 text-decoration-none"
                            onClick={() => openEditModal(item)}
                          >
                            <FontAwesomeIcon icon={faEdit} size="sm" />
                          </Button>
                          <Button
                            type="button"
                            variant="link"
                            className="p-0 text-decoration-none"
                            onClick={() => openViewModal(item)}
                          >
                            <FontAwesomeIcon icon={faEye} />
                          </Button>
                        </div>
                      </td>
                    </tr>
                  ))
                ) : (
                  <tr>
                    <td colSpan={4} className="text-center text-muted py-5">
                      No courses found.
                    </td>
                  </tr>
                )}
              </tbody>
            </Table>
          </div>

          <div className="d-flex justify-content-between align-items-center mt-3 flex-wrap gap-2">
            <div className="text-muted small">
              Showing {start} to {end} of {pagination.total} entries
            </div>

            <div className="d-flex align-items-center gap-2 flex-wrap">
              <Button
                type="button"
                variant="outline-primary"
                disabled={pagination.current_page <= 1 || loading}
                onClick={() => {
                  clearSearchDebounce();
                  fetchCourses(Math.max(1, pagination.current_page - 1), pagination.per_page, search);
                }}
                className="course-page-btn"
              >
                <FontAwesomeIcon icon={faChevronLeft} />
              </Button>

              {getPageItems().map((item, index) =>
                item === "..." ? (
                  <span key={`dots-${index}`} className="text-muted px-1">
                    ...
                  </span>
                ) : (
                  <Button
                    key={item}
                    type="button"
                    variant="outline-primary"
                    disabled={loading}
                    onClick={() => {
                      clearSearchDebounce();
                      fetchCourses(item, pagination.per_page, search);
                    }}
                    className={`course-page-btn ${item === pagination.current_page ? "course-page-btn-active" : ""}`}
                  >
                    {item}
                  </Button>
                )
              )}

              <Button
                type="button"
                variant="outline-primary"
                disabled={pagination.current_page >= pagination.last_page || loading}
                onClick={() => {
                  clearSearchDebounce();
                  fetchCourses(Math.min(pagination.last_page, pagination.current_page + 1), pagination.per_page, search);
                }}
                className="course-page-btn"
              >
                <FontAwesomeIcon icon={faChevronRight} />
              </Button>
            </div>
          </div>
        </Card.Body>
      </Card>

      <Modal show={showModal} onHide={closeModal} centered size="lg">
        <Form onSubmit={handleSubmit}>
          <Modal.Header closeButton>
            <Modal.Title>
              {editingCourse ? "Edit Course" : "Add Course"}
            </Modal.Title>
          </Modal.Header>

          <Modal.Body>
            <Row className="g-3">
              {/* Course Title */}
              <Col md={12}>
                <Form.Group className="mb-3" controlId="courseTitle">
                  <Form.Label className="fw-semibold">
                    Course Title <span className="text-danger">*</span>
                  </Form.Label>
                  <Form.Control
                    name="title"
                    type="text"
                    placeholder="Enter course title"
                    value={formData.title}
                    onChange={handleChange}
                    style={{ boxShadow: "none" }}
                    isInvalid={!!errors.title}
                  />
                  <Form.Control.Feedback type="invalid">
                    {errors.title}
                  </Form.Control.Feedback>
                </Form.Group>
              </Col>

              {/* Price */}
              <Col md={6}>
                <Form.Group className="mb-0" controlId="coursePrice">
                  <Form.Label className="fw-semibold">
                    Price <span className="text-danger">*</span>
                  </Form.Label>
                  <Form.Control
                    name="price"
                    type="text"
                    inputMode="numeric"
                    placeholder="Enter price"
                    value={formData.price}
                    onChange={handleChange}
                    style={{ boxShadow: "none" }}
                    isInvalid={!!errors.price}
                  />
                  <Form.Control.Feedback type="invalid">
                    {errors.price}
                  </Form.Control.Feedback>
                </Form.Group>
              </Col>

              {/* status */}
              <Col md={6}>
                <Form.Group controlId="courseStatus">
                  <Form.Label className="fw-semibold">Status <span className="text-danger">*</span></Form.Label>
                  <Form.Select
                    name="status"
                    value={formData.status}
                    onChange={handleChange}
                    style={{ boxShadow: "none" }}
                    isInvalid={!!errors.status}
                  >
                    <option value="">Select status</option>
                    <option value="active">Active</option>
                    <option value="inactive">Inactive</option>
                  </Form.Select>
                  <Form.Control.Feedback type="invalid">
                    {errors.status}
                  </Form.Control.Feedback>
                </Form.Group>
              </Col>

              {/* Start Date */}
              <Col md={6}>
                <Form.Group controlId="startDate">
                  <Form.Label className="fw-semibold">Start Date <span className="text-danger">*</span></Form.Label>
                  <Form.Control
                    ref={startDateRef}
                    type="date"
                    name="start_date"
                    value={formData.start_date}
                    onChange={handleChange}
                    onClick={() => startDateRef.current?.showPicker?.()}
                    style={{ boxShadow: "none" }}
                    isInvalid={!!errors.start_date}
                  />
                  <Form.Control.Feedback type="invalid">
                    {errors.start_date}
                  </Form.Control.Feedback>
                </Form.Group>
              </Col>

              {/* End Date */}
              <Col md={6}>
                <Form.Group controlId="endDate">
                  <Form.Label className="fw-semibold">End Date</Form.Label>
                  <Form.Control
                    ref={endDateRef}
                    type="date"
                    name="end_date"
                    value={formData.end_date}
                    onChange={handleChange}
                    min={formData.start_date || undefined}
                    disabled={!formData.start_date}
                    onClick={() => {
                      if (formData.start_date) {
                        endDateRef.current?.showPicker?.();
                      }
                    }}
                    style={{ boxShadow: "none" }}
                    // isInvalid={!!errors.end_date}
                  />
                  {/* <Form.Control.Feedback type="invalid">
                    {errors.end_date}
                  </Form.Control.Feedback> */}
                </Form.Group>
              </Col>


              {/* Enrollment Start Date */}
              <Col md={6}>
                <Form.Group controlId="enrollmentStartDate">
                  <Form.Label className="fw-semibold">Enrollment Start Date</Form.Label>
                  <Form.Control
                    ref={enrollmentStartDateRef}
                    type="date"
                    name="enrollment_start_date"
                    value={formData.enrollment_start_date}
                    onChange={handleChange}
                    max={formData.start_date || undefined}
                    disabled={!formData.start_date}
                     onClick={() => {
                      if (formData.start_date) {
                        enrollmentStartDateRef.current?.showPicker?.();
                      }
                    }}
                    style={{ boxShadow: "none" }}
                  />
                </Form.Group>
              </Col>

              {/* Enrollment End Date */}
              <Col md={6}>
                <Form.Group controlId="enrollmentEndDate">
                  <Form.Label className="fw-semibold">Enrollment End Date</Form.Label>
                  <Form.Control
                    ref={enrollmentEndDateRef}
                    type="date"
                    name="enrollment_end_date"
                    value={formData.enrollment_end_date}
                    onChange={handleChange}
                    min={formData.enrollment_start_date || undefined}
                    disabled={!formData.enrollment_start_date}
                    onClick={() => {
                      if (formData.enrollment_start_date) {
                        enrollmentEndDateRef.current?.showPicker?.();
                      }
                    }}
                    style={{ boxShadow: "none" }}
                  />
                </Form.Group>
              </Col>

              {/* Description */}
              <Col md={12}>
                <Form.Group controlId="courseDescription">
                  <Form.Label className="fw-semibold">Description</Form.Label>
                  <Form.Control
                    as="textarea"
                    rows={3}
                    name="description"
                    placeholder="Enter course description"
                    value={formData.description}
                    onChange={handleChange}
                  />
                </Form.Group>
              </Col>
            </Row>
          </Modal.Body>

          <Modal.Footer>
            <Button onClick={closeModal} disabled={submitting}>
              Cancel
            </Button>
            <Button type="submit" style={ACTIVE_BUTTON_STYLE} disabled={submitting}>
              {submitting ? "Saving..." : editingCourse ? "Update Course" : "Add Course"}
            </Button>
          </Modal.Footer>
        </Form>
      </Modal>

      <Modal show={showViewModal} onHide={() => setShowViewModal(false)} centered size="lg">
        <Modal.Header closeButton>
          <Modal.Title>View Course</Modal.Title>
        </Modal.Header>

        <Modal.Body>
          <Row className="g-3 p-2">
            <Col md={12}>
              <Form.Group>
                <Form.Label className="fw-semibold">Course Title</Form.Label>
                <Form.Control value={viewCourse?.title || "N/A"} disabled />
              </Form.Group>
            </Col>

            <Col md={6}>
              <Form.Group>
                <Form.Label className="fw-semibold">Price</Form.Label>
                <Form.Control value={viewCourse?.price || "N/A"} disabled />
              </Form.Group>
            </Col>

            <Col md={6}>
              <Form.Group>
                <Form.Label className="fw-semibold">Status</Form.Label>
                <Form.Control value={capitalize(viewCourse?.status || "N/A")} disabled />
              </Form.Group>
            </Col>

            <Col md={6}>
              <Form.Group>
                <Form.Label className="fw-semibold">Start Date</Form.Label>
                <Form.Control value={viewCourse?.start_date || "N/A"} disabled />
              </Form.Group>
            </Col>

            <Col md={6}>
              <Form.Group>
                <Form.Label className="fw-semibold">End Date</Form.Label>
                <Form.Control value={viewCourse?.end_date || "N/A"} disabled />
              </Form.Group>
            </Col>

            <Col md={6}>
              <Form.Group>
                <Form.Label className="fw-semibold">Enrollment Start Date</Form.Label>
                <Form.Control value={viewCourse?.enrollment_start_date || "N/A"} disabled />
              </Form.Group>
            </Col>

            <Col md={6}>
              <Form.Group>
                <Form.Label className="fw-semibold">Enrollment End Date</Form.Label>
                <Form.Control value={viewCourse?.enrollment_end_date || "N/A"} disabled />
              </Form.Group>
            </Col>

            <Col md={12}>
              <Form.Group>
                <Form.Label className="fw-semibold">Description</Form.Label>
                <Form.Control as="textarea" rows={3} value={viewCourse?.description || "N/A"} disabled />
              </Form.Group>
            </Col>
          </Row>
        </Modal.Body>

        <Modal.Footer>
          <Button onClick={() => setShowViewModal(false)}>Close</Button>
        </Modal.Footer>
      </Modal>

      <style>{`
        .course-header {
          display: flex;
          align-items: center;
          justify-content: space-between;
          gap: 16px;
          padding: 24px 0;
        }

        .course-header-title {
          flex: 0 0 auto;
        }

        .course-header-actions {
          display: flex;
          align-items: center;
          justify-content: flex-end;
          gap: 12px;
          flex: 1;
        }

        .course-search {
          width: 100%;
          max-width: 300px;
          flex: 0 1 360px;
        }

        .course-add-btn {
          white-space: nowrap;
          flex: 0 0 auto;
          padding: 10px 20px;
        }

        .action-buttons {
  flex-wrap: nowrap !important;
  white-space: nowrap;
}

.action-buttons .btn {
  white-space: nowrap;
  flex-shrink: 0;
}

        /* Mobile */
        @media (max-width: 767.98px) {
          .course-header {
            flex-direction: column;
            align-items: stretch;
          }

          .course-header-title {
            width: 100%;
          }

          .course-header-actions {
            width: 100%;
            flex-direction: column;
            align-items: stretch;
          }

          .course-search {
            width: 100%;
            max-width: none;
            flex: 1 1 auto;
          }

          .course-add-btn {
            width: 100%;
          }

          
  /* Updated mobile top row */
  .course-list-top {
    display: flex;
    flex-direction: row !important;
    align-items: center;
    justify-content: space-between;
    gap: 10px;
    flex-wrap: nowrap;
  }

  .course-list-actions {
    display: flex;
    flex-direction: row !important;
    gap: 8px;
    flex-wrap: nowrap;
    margin-left: auto;
  }

  .course-list-actions .btn {
    width: auto !important;
    white-space: nowrap;
    padding: 6px 12px;
    font-size: 14px;
  }
        }
      `}</style>
    </>
  );
}